/**
 * Moving plates for the opening scenes.
 *
 * A video element rather than a background image, with the still it replaces set as the
 * poster. That still is not decoration: it is what everyone sees who cannot or should
 * not get the video — reduced motion, a blocked autoplay, a data-saver connection, a
 * decode failure. The scene has to be finished before the video arrives, not after.
 *
 * Nothing is fetched until its scene is near. Three 720p clips are several megabytes,
 * and the page opens on the first one — pulling all of them at load would spend the
 * visitor's connection on scenes they may never reach.
 */

import { motionAllowed } from "./motion";

export type SceneVideo = { element: HTMLElement; dispose: () => void };

// Far enough ahead that the first frame is decoded before the scene is reached, close
// enough that scrolling past the top of the page does not fetch every clip.
const PRELOAD_MARGIN = "150% 0px";

/**
 * Clips live in public/ rather than src/, so Vite neither fingerprints nor rewrites
 * them and a bare path would resolve against the current URL. Deployed under a nested
 * base the index is reachable at a sub-path, and every clip would 404 against the host
 * root — which is invisible here, because the poster stays up and the scene still looks
 * finished.
 */
export function publicUrl(path: string): string {
  return new URL(path, new URL(import.meta.env.BASE_URL, window.location.href)).href;
}

export function createSceneVideo(
  source: string,
  poster: string,
  kind: string,
): SceneVideo {
  const holder = document.createElement("div");
  holder.className = `scene__layer scene__layer--${kind}`;
  holder.setAttribute("aria-hidden", "true");
  // The poster lives on the holder as well as the element: a video with preload="none"
  // paints nothing at all until it is told to load, so without this the scene is empty
  // for as long as the fetch takes.
  holder.style.backgroundImage = `url(${poster})`;

  const still = !motionAllowed();
  if (still) {
    // No element at all, so nothing can autoplay and nothing is downloaded. The poster
    // is already showing and it is the whole scene.
    return { element: holder, dispose: () => undefined };
  }

  const video = document.createElement("video");
  video.className = "scene__video";
  video.poster = poster;
  video.muted = true;
  // Required by iOS or the video takes over the screen in a native player.
  video.playsInline = true;
  video.loop = true;
  video.preload = "none";
  video.tabIndex = -1;
  video.setAttribute("aria-hidden", "true");
  holder.append(video);

  let loaded = false;
  let visible = false;

  /**
   * Play only when the scene is on screen and there is something to play.
   *
   * Both conditions arrive independently and in either order. The first scene is
   * on screen the instant it renders, which is before it has a source at all — a
   * play() there does nothing, and without a retry when the data lands the opening
   * shot stays frozen on its poster until the visitor scrolls away and back.
   */
  const tryPlay = (): void => {
    if (!visible || !loaded) return;
    // Autoplay can still be refused. The poster stays up if it is, which is why the
    // rejection is swallowed rather than reported.
    void video.play().catch(() => undefined);
  };

  video.addEventListener("loadeddata", tryPlay);

  const near = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting || loaded) continue;
        loaded = true;
        video.preload = "auto";
        video.src = publicUrl(source);
        tryPlay();
        near.disconnect();
      }
    },
    { rootMargin: PRELOAD_MARGIN },
  );
  near.observe(holder);

  const onscreen = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        visible = entry.isIntersecting;
        if (visible) tryPlay();
        else video.pause();
      }
    },
    { threshold: 0 },
  );
  onscreen.observe(holder);

  return {
    element: holder,
    dispose: () => {
      near.disconnect();
      onscreen.disconnect();
      video.removeEventListener("loadeddata", tryPlay);
      video.pause();
      // Drops the buffered data. Without it the decoder keeps the clip resident after
      // the index is torn down and the visitor has walked into an exhibition.
      video.removeAttribute("src");
      video.load();
    },
  };
}
